<template>
  <div>
    <div>
      <div class="management-potential-report-page" id="print_element">
        <div v-if="isPreloader" class="management-potential-report-page__preloader">
          <Preloader />
        </div>

        <template v-else>
          <div>
            <ReportDetailsHeader :title="reportDetails.type === 'group' ? 'Сравнение групп' : 'Аналитика изменений в ЭИ'" :reportType="'comparison'" />
          </div>

          <ReferenceSection
              title="Управленческий потенциал"
              :opened="openedSections.managementPotential"
              @toggle="toggleSection('managementPotential')"
              :collapsible="false"
          >
            <ManagementPotentialInstruction class="instruction-section" />

            <div class="management-potential-report-page__section">
              <a-card
                  :bordered="true"
                  class="management-potential-report-page__chart-card pdf-avoid-break"
              >
                <ManagementPotentialCompareChart
                    title="Количество сотрудников на каждом уровне Управленческого потенциала"
                    :previous-label="previousPeriod.label"
                    :current-label="currentPeriod.label"
                    :previous-color="previousPeriod.color"
                    :current-color="currentPeriod.color"
                    :items="managementPotentialBars"
                />
              </a-card>

              <div class="management-potential-report-page__subsection">
                <h3 class="management-potential-report-page__subsection-title">
                  Зеленые и красные флаги
                </h3>

                <ManagementFlagsComparisonBlock
                    :previous-label="previousPeriod.shortLabel"
                    :current-label="currentPeriod.shortLabel"
                    :previous-groups="previousFlags.groups"
                    :previous-segments="previousFlags.segments"
                    :current-groups="currentFlags.groups"
                    :current-segments="currentFlags.segments"
                />
              </div>
            </div>
          </ReferenceSection>

          <ReferenceSection
              title="Эмоциональный интеллект"
              :opened="openedSections.emotionalIntelligence"
              @toggle="toggleSection('emotionalIntelligence')"
              :showLine="false"
              :collapsible="false"
          >
            <div class="management-potential-report-page__section">
              <EiGeneralScale
                  class="pdf-avoid-break"
                  :show-title="false"
                  card-title="Средний общий ЭИ"
                  description="Уровень совокупности когнитивных способностей: саморегуляция, влияние на эмоции других, эмпатия, анализ изменений эмоциональных реакций, сознательное применение энергии эмоций для усиления деятельности и мышления."
                  :compare-value="eiGeneralScale.compareValue"
                  :value="eiGeneralScale.value"
                  :compare-label="previousPeriod.label"
                  :main-label="currentPeriod.label"
                  :compare-color="previousPeriod.color"
                  :main-color="currentPeriod.color"
              />

              <div class="management-potential-report-page__ei-chart-grid">
                <a-card
                    :bordered="true"
                    class="management-potential-report-page__chart-card management-potential-report-page__chart-card--small pdf-avoid-break"
                >
                  <EiLevelEmployeesCompareChart
                      title="Количество сотрудников на каждом уровне Общего ЭИ"
                      :previous-label="previousPeriod.label"
                      :current-label="currentPeriod.label"
                      :previous-color="previousPeriod.color"
                      :current-color="currentPeriod.color"
                      :items="eiLevelBars"
                  />
                </a-card>
              </div>
            </div>
          </ReferenceSection>

          <ReferenceSection
              title="Стратеги и Драйверы"
              :opened="openedSections.strategistsAndDrivers"
              @toggle="toggleSection('strategistsAndDrivers')"
              :showLine="false"
              :collapsible="false"
          >
            <div class="management-potential-report-page__section">
              <div class="management-potential-report-page__notice pdf-avoid-break">
                <InfoCircleFilled class="management-potential-report-page__notice-icon" />

                <span>
                  Чем меньше разрыв между доменами, тем выше личная эффективность.
                  Сильный разрыв ведет к ошибкам в оценке ситуаций.
                </span>
              </div>

              <RoleDistributionComparisonBlock
                  :previous-label="previousPeriod.shortLabel"
                  :current-label="currentPeriod.shortLabel"
                  :previous-items="roleDistribution.previous"
                  :current-items="roleDistribution.current"
              />

              <div class="management-potential-report-page__comparison-grid">
                <DomainDisbalanceCard
                    class="pdf-avoid-break"
                    :period-label="previousPeriod.shortLabel"
                    title="Уровень дисбаланса между доменами"
                    description="Показывает средний разрыв баллов в каждом домене и оценивает гибкость поведения."
                    :rows="domainDisbalance.previous"
                    :compare-rows="domainDisbalance.current"
                />

                <DomainDisbalanceCard
                    class="pdf-avoid-break"
                    :period-label="currentPeriod.shortLabel"
                    tag-type="current"
                    title="Уровень дисбаланса между доменами"
                    description="Показывает средний разрыв баллов в каждом домене и оценивает гибкость поведения."
                    :rows="domainDisbalance.current"
                    :compare-rows="domainDisbalance.previous"
                />
              </div>

              <div class="management-potential-report-page__subsection">
                <h3 class="management-potential-report-page__subsection-title">
                  Сильные стороны
                </h3>

                <StrongSidesComparisonBlock
                    :previous-label="previousPeriod.shortLabel"
                    :current-label="currentPeriod.shortLabel"
                    :previous-sections="strongSides.previous"
                    :current-sections="strongSides.current"
                />
              </div>

              <BranchDonutComparisonBlock
                  :previous-label="previousPeriod.shortLabel"
                  :current-label="currentPeriod.shortLabel"
                  title="Ведущие ветви ЭИ"
                  description="Количество человек с лидерским показателем в каждой ветви."
                  :previous-items="leadingBranches.previous"
                  :current-items="leadingBranches.current"
              />

              <div class="management-potential-report-page__subsection">
                <h3 class="management-potential-report-page__subsection-title">
                  Зоны роста
                </h3>

                <BranchScaleComparisonBlock
                    :previous-label="previousPeriod.shortLabel"
                    :current-label="currentPeriod.shortLabel"
                    title="Зоны роста в ветвях ЭИ"
                    :previous-sections="growthZones.previous"
                    :current-sections="growthZones.current"
                />
              </div>

              <BranchDonutComparisonBlock
                  :previous-label="previousPeriod.shortLabel"
                  :current-label="currentPeriod.shortLabel"
                  title="Приоритеты развития"
                  description="Количество сотрудников, для которых эта ветвь — самая слабая."
                  :previous-items="developmentPriorities.previous"
                  :current-items="developmentPriorities.current"
              />

              <div class="management-potential-report-page__subsection">
                <h3 class="management-potential-report-page__subsection-title">
                  ЭИ-профиль с сильными сторонами и зонами роста
                </h3>

                <EiProfileGrowthComparisonBlock
                    :previous-label="previousPeriod.shortLabel"
                    :current-label="currentPeriod.shortLabel"
                    :previous-sections="eiGrowthProfile.previous"
                    :current-sections="eiGrowthProfile.current"
                />
              </div>

              <EiProfileDetailsComparisonBlock
                  :previous-label="previousPeriod.shortLabel"
                  :current-label="currentPeriod.shortLabel"
                  :previous-sections="eiDetailsProfile.previous"
                  :current-sections="eiDetailsProfile.current"
              />

              <div class="management-potential-report-page__footer" data-html2canvas-ignore>
                <a-button class="secondary-button" @click="scrollTop">
                  Наверх
                </a-button>
              </div>
            </div>
          </ReferenceSection>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import {
  DownloadOutlined,
  InfoCircleFilled,
  LeftOutlined,
} from '@ant-design/icons-vue'
import html2pdf from 'html2pdf.js'

import Preloader from '@/components/Technical/Preloader.vue'
import ReferenceSection from '@/views/Report/NewProfileReport/ReferenceSection.vue'
import ReportNextStepPromotionCard from '@/components/Cards/ReportNextStepPromotionCard.vue'
import EiProfileGrowthComparisonBlock from '@/views/Report/CompareReport/EiProfileGrowthComparisonBlock.vue'
import EiProfileDetailsComparisonBlock from '@/views/Report/CompareReport/EiProfileDetailsComparisonBlock.vue'
import BranchDonutComparisonBlock from '@/views/Report/CompareReport/BranchDonutComparisonBlock.vue'
import BranchScaleComparisonBlock from '@/views/Report/CompareReport/BranchScaleComparisonBlock.vue'
import StrongSidesComparisonBlock from '@/views/Report/CompareReport/StrongSidesComparisonBlock.vue'
import DomainDisbalanceCard from '@/views/Report/CompareReport/DomainDisbalanceCard.vue'
import RoleDistributionComparisonBlock from '@/views/Report/CompareReport/RoleDistributionComparisonBlock.vue'
import EiLevelEmployeesCompareChart from '@/views/Report/CompareReport/EiLevelEmployeesCompareChart.vue'
import EiGeneralScale from '@/components/Analytics/GeneralBenchmarkDetail.vue'
import ManagementFlagsComparisonBlock from '@/views/Report/CompareReport/ManagementFlagsComparisonBlock.vue'
import ManagementPotentialCompareChart from '@/views/Report/CompareReport/PotentialChart.vue'
import ManagementPotentialInstruction from '@/views/Report/ManagementPotentialReport/ManagementPotentialInstruction.vue'
import ReportDetailsHeader from '@/feature/analytics/report-parameters/ReportDetailsHeader.vue'
import { useStore } from 'vuex'

export default {
  name: 'ManagementPotentialReportPage',

  components: {
    ReportDetailsHeader,
    ManagementPotentialInstruction,
    ManagementPotentialCompareChart,
    ManagementFlagsComparisonBlock,
    EiGeneralScale,
    EiLevelEmployeesCompareChart,
    RoleDistributionComparisonBlock,
    DomainDisbalanceCard,
    StrongSidesComparisonBlock,
    BranchScaleComparisonBlock,
    BranchDonutComparisonBlock,
    EiProfileDetailsComparisonBlock,
    EiProfileGrowthComparisonBlock,
    LeftOutlined,
    DownloadOutlined,
    InfoCircleFilled,
    Preloader,
    ReferenceSection,
    ReportNextStepPromotionCard,
  },

  setup() {
    const router = useRouter()
    const store = useStore()

    const RED_COLOR = '#F55565'
    const GREY_COLOR = '#D5DBE2'
    const BLUE_COLOR = '#85A5FF'
    const EI_GREEN_COLOR = '#99DA6E'
    const BRANCH_HIGHLIGHT_GAP = 2

    const BRANCH_COLORS = {
      recognition: '#6C5CE7',
      use: '#B6AFFF',
      analysis: '#00B5AD',
      management: '#71E5E0',
    }

    const POTENTIAL_LEVELS = [
      {
        types: ['LEVEL_POTENTIAL_LOW'],
        key: 'low',
        label: 'Низкий',
      },
      {
        types: ['LEVEL_POTENTIAL_MODERATE_RISKS'],
        key: 'medium',
        label: 'Умеренный',
      },
      {
        types: ['LEVEL_POTENTIAL_MODERATE'],
        key: 'medium',
        label: 'Средний',
      },
      {
        types: ['LEVEL_POTENTIAL_HIGHEST'],
        key: 'high',
        label: 'Высокий',
      },
      {
        types: ['LEVEL_POTENTIAL_OVER_HIGH'],
        key: 'highest',
        label: 'Максимальный',
      },
    ]

    const EI_LEVELS = [
      { key: 'low', label: 'Низкий' },
      { key: 'medium', label: 'Средний' },
      { key: 'high', label: 'Высокий' },
      { key: 'highest', label: 'Высший' },
    ]

    const ROLE_ITEMS = [
      { key: 'drivers', label: 'Драйверы', color: '#A29BFE' },
      { key: 'strategists', label: 'Стратеги', color: '#71E5E0' },
      { key: 'balanced', label: 'Сбалансированные', color: GREY_COLOR },
    ]

    const DOMAIN_DISBALANCE_ITEMS = [
      { key: 'drivers', label: 'Драйверы' },
      { key: 'strategists', label: 'Стратеги' },
    ]

    const BRANCH_ITEMS = [
      {
        key: 'branch_1',
        countKey: 'branch_1_count',
        label: 'Распознавание эмоций',
        color: BRANCH_COLORS.recognition,
        helpText: 'Способность распознавать эмоции по лицам, ситуациям, среде и контексту.',
      },
      {
        key: 'branch_2',
        countKey: 'branch_2_count',
        label: 'Использование эмоций',
        color: BRANCH_COLORS.use,
        helpText: 'Способность использовать эмоции как ресурс мышления и деятельности.',
      },
      {
        key: 'branch_3',
        countKey: 'branch_3_count',
        label: 'Анализ эмоций',
        color: BRANCH_COLORS.analysis,
        helpText: 'Способность понимать причины, переходы и динамику эмоциональных состояний.',
      },
      {
        key: 'branch_4',
        countKey: 'branch_4_count',
        label: 'Управление эмоциями',
        color: BRANCH_COLORS.management,
        helpText: 'Способность регулировать свои эмоции и влиять на эмоции других.',
      },
    ]

    const DETAILS_HELP_TEXTS = {
      section_1: 'Раздел направлен на эмоциональное оценивание лиц.',
      section_5: 'Раздел направлен на измерение способности определять эмоции в окружающем пространстве, среде и произведениях искусства.',
      section_9a: 'Раздел направлен на оценку способности распознавать эмоции в динамике и с учетом дополнительных факторов.',
      section_2: 'Раздел направлен на измерение способности определять эмоции, эффективные для содействия мышлению в конкретной деятельности.',
      section_6: 'Раздел направлен на измерение способности описывать свои эмоциональные состояния и почувствовать эмоции другого.',
      section_3: 'Раздел направлен на измерение способности понимать особенности перехода эмоций друг в друга.',
      section_7: 'Раздел направлен на диагностику способности различать эмоции, входящие в комплексные чувства.',
      section_9b: 'Раздел направлен на измерение способности оценивать динамику эмоциональных состояний с учетом внешних факторов.',
      section_4: 'Раздел направлен на изучение способности регулировать собственные эмоциональные состояния.',
      section_8: 'Раздел направлен на измерение способности регулировать эмоциональные состояния других людей.',
    }

    const SECTION_ITEMS = [
      { key: 'section_1', dataKey: 'company_section_1', label: 'Распознавание эмоций в лицах' },
      { key: 'section_5', dataKey: 'company_section_5', label: 'Распознавание эмоций в среде' },
      { key: 'section_9a', dataKey: 'company_section_9a', label: 'Распознавание эмоций в контексте ситуаций' },
      { key: 'section_2', dataKey: 'company_section_2', label: 'Применение эмоций в действиях' },
      { key: 'section_6', dataKey: 'company_section_6', label: 'Эмпатия' },
      { key: 'section_3', dataKey: 'company_section_3', label: 'Анализ изменений эмоций' },
      { key: 'section_7', dataKey: 'company_section_7', label: 'Анализ соединения эмоций в чувства' },
      { key: 'section_9b', dataKey: 'company_section_9b', label: 'Анализ динамики эмоций в контексте ситуаций' },
      { key: 'section_4', dataKey: 'company_section_4', label: 'Регуляция своих эмоций' },
      { key: 'section_8', dataKey: 'company_section_8', label: 'Влияние на эмоции других' },
    ]

    const isPreloader = ref(false)
    const isPrinting = ref(false)

    const reportDetails = computed(() => {
      return store.state.analytics.currentReportDetails ?? {
        type: null,
        name: null,
        groupName: null,
        filterName: null,
        employeeCount: null,
        dateString: null,
        dateInterval: null,
      }
    })

    const reportData = computed(() => store.state.analytics.reportData ?? {})

    const firstReportData = computed(() => reportData.value?.first ?? {})
    const secondReportData = computed(() => reportData.value?.second ?? {})

    const openedSections = reactive({
      managementPotential: true,
      emotionalIntelligence: true,
      strategistsAndDrivers: true,
    })

    const periodLabels = computed(() => {
      const details = reportDetails.value ?? {}

      if (details.type === 'group') {
        const rawName = details.name ?? details.groupName ?? details.filterName ?? ''
        const [first = '', second = ''] = splitBySlash(rawName)

        return {
          previous: first,
          current: second,
        }
      }

      if (details.type === 'dynamic') {
        const [first = '', second = ''] = splitBySlash(details.dateInterval ?? '')

        return {
          previous: first ? `Было: ${first}` : '',
          current: second ? `Стало: ${second}` : '',
        }
      }

      return {
        previous: '',
        current: '',
      }
    })

    const previousPeriod = computed(() => ({
      label: periodLabels.value.previous,
      shortLabel: periodLabels.value.previous,
      color: GREY_COLOR,
    }))

    const currentPeriod = computed(() => ({
      label: periodLabels.value.current,
      shortLabel: periodLabels.value.current,
      color: BLUE_COLOR,
    }))

    const managementPotentialBars = computed(() => {
      return buildManagementPotentialBars(firstReportData.value, secondReportData.value)
    })

    const eiGeneralScale = computed(() => ({
      compareValue: roundScore(firstReportData.value?.ei?.average_ei_profile),
      value: roundScore(secondReportData.value?.ei?.average_ei_profile),
    }))

    const eiLevelBars = computed(() => {
      return buildEiLevelBars(firstReportData.value, secondReportData.value)
    })

    const roleDistribution = computed(() => ({
      previous: buildRoleDistribution(firstReportData.value),
      current: buildRoleDistribution(secondReportData.value),
    }))

    const domainDisbalance = computed(() => ({
      previous: buildDomainDisbalance(firstReportData.value),
      current: buildDomainDisbalance(secondReportData.value),
    }))

    const strongSides = computed(() => ({
      previous: buildBranchScaleSection(
          firstReportData.value?.ei?.strengths?.branches,
          secondReportData.value?.ei?.strengths?.branches
      ),
      current: buildBranchScaleSection(
          secondReportData.value?.ei?.strengths?.branches,
          firstReportData.value?.ei?.strengths?.branches
      ),
    }))

    const leadingBranches = computed(() => ({
      previous: buildBranchCountItems(firstReportData.value?.ei?.strengths?.leading),
      current: buildBranchCountItems(secondReportData.value?.ei?.strengths?.leading),
    }))

    const growthZones = computed(() => ({
      previous: buildBranchScaleSection(
          firstReportData.value?.ei?.growth_zones?.branches,
          secondReportData.value?.ei?.growth_zones?.branches,
          true
      ),
      current: buildBranchScaleSection(
          secondReportData.value?.ei?.growth_zones?.branches,
          firstReportData.value?.ei?.growth_zones?.branches,
          true
      ),
    }))

    const developmentPriorities = computed(() => ({
      previous: buildBranchCountItems(firstReportData.value?.ei?.growth_zones?.development_priorities),
      current: buildBranchCountItems(secondReportData.value?.ei?.growth_zones?.development_priorities),
    }))

    const eiGrowthProfile = computed(() => ({
      previous: buildEiGrowthProfile(firstReportData.value),
      current: buildEiGrowthProfile(secondReportData.value),
    }))

    const eiDetailsProfile = computed(() => ({
      previous: buildEiDetailsProfile(firstReportData.value),
      current: buildEiDetailsProfile(secondReportData.value),
    }))

    const previousFlags = computed(() => {
      return buildFlags(firstReportData.value?.management?.detailed_flags)
    })

    const currentFlags = computed(() => {
      return buildFlags(
          secondReportData.value?.management?.detailed_flags,
          firstReportData.value?.management?.detailed_flags
      )
    })

    const toggleSection = (key) => {
      openedSections[key] = !openedSections[key]
    }

    const print = () => {
      isPrinting.value = true

      html2pdf(document.getElementById('print_element'), {
        autoPaging: 'text',
        margin: [12, 8, 15, 8],
        filename: `management_potential_${Date.now()}.pdf`,
        html2canvas: {
          dpi: 192,
          scale: 2,
          windowWidth: 1000,
        },
        pagebreak: {
          mode: ['css', 'legacy'],
          before: ['.pdf-page-break'],
          avoid: ['.pdf-avoid-break'],
        },
      }).then(() => {
        isPrinting.value = false
      })
    }

    const goBack = (url) => {
      if (window.history.length > 1) {
        router.back()
      } else {
        router.push(url)
      }
    }

    const scrollTop = () => {
      const container = document.querySelector('.page-container')

      if (container) {
        container.scrollTo({
          top: 0,
          behavior: 'smooth',
        })

        return
      }

      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
    }

    function splitBySlash(value) {
      if (!value) {
        return ['', '']
      }

      return String(value)
          .split('/')
          .map((item) => item.trim())
    }

    function roundScore(value) {
      if (value === null || value === undefined || value === '') {
        return null
      }

      const number = Number(value)

      if (Number.isNaN(number)) {
        return null
      }

      return Math.round(number)
    }

    function getCount(value) {
      if (value === null || value === undefined || value === '') {
        return 0
      }

      const number = Number(value)

      if (Number.isNaN(number)) {
        return 0
      }

      return Math.round(number)
    }

    function getDifference(currentValue, previousValue) {
      const current = roundScore(currentValue)
      const previous = roundScore(previousValue)

      if (current === null || previous === null) {
        return null
      }

      return current - previous
    }

    function buildManagementPotentialBars(firstData, secondData) {
      const firstItems = firstData?.management?.management_potential_score ?? []
      const secondItems = secondData?.management?.management_potential_score ?? []

      const firstMap = buildArrayMap(firstItems, 'type')
      const secondMap = buildArrayMap(secondItems, 'type')

      return POTENTIAL_LEVELS.map((level) => {
        const previousValue = level.types.reduce((total, type) => {
          return total + getCount(firstMap[type]?.count)
        }, 0)

        const currentValue = level.types.reduce((total, type) => {
          return total + getCount(secondMap[type]?.count)
        }, 0)

        return {
          key: level.key,
          label: level.label,
          previousValue,
          currentValue,
        }
      })
    }

    function buildEiLevelBars(firstData, secondData) {
      const firstLevels = firstData?.ei?.count_employee_level_ei ?? {}
      const secondLevels = secondData?.ei?.count_employee_level_ei ?? {}

      return EI_LEVELS.map((level) => ({
        key: level.key,
        label: level.label,
        previousValue: getCount(firstLevels[level.key]),
        currentValue: getCount(secondLevels[level.key]),
      }))
    }

    function buildRoleDistribution(data) {
      const countEmployee = data?.ei?.strategies_and_drivers?.count_employee ?? {}

      return ROLE_ITEMS.map((item) => ({
        key: item.key,
        label: item.label,
        value: getCount(countEmployee[item.key]),
        color: item.color,
      }))
    }

    function buildDomainDisbalance(data) {
      const imbalance = data?.ei?.strategies_and_drivers?.level_imbalance ?? {}

      return DOMAIN_DISBALANCE_ITEMS.map((item) => ({
        key: item.key,
        label: item.label,
        average: roundScore(imbalance[item.key]?.avg),
        min: getCount(imbalance[item.key]?.min),
        max: getCount(imbalance[item.key]?.max),
      }))
    }

    function getRankedRowKeys(rows, direction, limit, excludedKeys = new Set()) {
      return new Set(
          rows
              .map((row, index) => ({ row, index }))
              .filter(({ row }) => row.value > 0 && !excludedKeys.has(row.key))
              .sort((a, b) => {
                const valueDiff = direction === 'highest'
                    ? b.row.value - a.row.value
                    : a.row.value - b.row.value

                return valueDiff || a.index - b.index
              })
              .slice(0, limit)
              .map(({ row }) => row.key)
      )
    }

    function getBranchHighlightKeys(rows, direction) {
      const sortedRows = rows
          .map((row, index) => ({ row, index }))
          .filter(({ row }) => row.value > 0)
          .sort((a, b) => {
            const valueDiff = direction === 'highest'
                ? b.row.value - a.row.value
                : a.row.value - b.row.value

            return valueDiff || a.index - b.index
          })

      if (!sortedRows.length) {
        return new Set()
      }

      const selectedKeys = new Set([sortedRows[0].row.key])
      const secondRow = sortedRows[1]

      if (
          secondRow &&
          Math.abs(sortedRows[0].row.value - secondRow.row.value) <= BRANCH_HIGHLIGHT_GAP
      ) {
        selectedKeys.add(secondRow.row.key)
      }

      return selectedKeys
    }

    function applyBranchScaleColors(rows, direction) {
      const highlightedKeys = getBranchHighlightKeys(rows, direction)
      const highlightColor = direction === 'highest' ? EI_GREEN_COLOR : RED_COLOR

      return rows.map((row) => ({
        ...row,
        color: highlightedKeys.has(row.key) ? highlightColor : GREY_COLOR,
      }))
    }

    function applyRankedScaleColors(rows, highestLimit, lowestLimit) {
      const highestKeys = getRankedRowKeys(rows, 'highest', highestLimit)
      const lowestKeys = getRankedRowKeys(rows, 'lowest', lowestLimit, highestKeys)

      return rows.map((row) => ({
        ...row,
        color: highestKeys.has(row.key)
            ? EI_GREEN_COLOR
            : lowestKeys.has(row.key)
                ? RED_COLOR
                : GREY_COLOR,
      }))
    }

    function applyStrengthGrowthProfileColors(sections) {
      return sections.map((section) => {
        if (section.key === 'domains' || section.key === 'branches') {
          return {
            ...section,
            rows: applyRankedScaleColors(section.rows, 1, 1),
          }
        }

        if (section.key === 'sections') {
          return {
            ...section,
            rows: applyRankedScaleColors(section.rows, 2, 2),
          }
        }

        return {
          ...section,
          rows: section.rows.map((row) => ({
            ...row,
            color: GREY_COLOR,
          })),
        }
      })
    }

    function buildBranchScaleSection(branches, compareBranches = null, growthZoneMode = false) {
      const hasCompareBranches = compareBranches !== null && compareBranches !== undefined

      return [
        {
          title: null,
          rows: BRANCH_ITEMS.map((branch) => {
            const value = roundScore(branches?.[branch.key])

            const difference = hasCompareBranches
                ? getDifference(branches?.[branch.key], compareBranches?.[branch.key])
                : null

            return buildScaleRow(
                branch.key,
                branch.label,
                value,
                GREY_COLOR,
                difference,
                branch.helpText
            )
          }),
        },
      ].map((section) => ({
        ...section,
        rows: applyBranchScaleColors(section.rows, growthZoneMode ? 'lowest' : 'highest'),
      }))
    }

    function buildBranchCountItems(items) {
      return BRANCH_ITEMS.map((branch) => ({
        key: branch.key,
        label: branch.label,
        value: getCount(items?.[branch.countKey]),
        color: branch.color,
      }))
    }

    function buildEiGrowthProfile(data) {
      const scores = data?.ei?.average_ei_scores ?? {}

      return applyStrengthGrowthProfileColors([
        {
          key: 'general',
          title: '',
          rows: [
            buildScaleRow(
                'total',
                'Общий ЭИ',
                roundScore(scores.total),
                GREY_COLOR,
                null,
                'Общий показатель эмоционального интеллекта.'
            ),
          ],
        },
        {
          key: 'domains',
          title: 'Домены',
          rows: [
            buildScaleRow(
                'domain_1',
                'Опытный домен',
                roundScore(scores.domain_1),
                GREY_COLOR,
                null,
                'Способность воспринимать эмоциональную информацию и реагировать на нее.'
            ),
            buildScaleRow(
                'domain_2',
                'Стратегический домен',
                roundScore(scores.domain_2),
                GREY_COLOR,
                null,
                'Способность понимать эмоциональную информацию и использовать ее стратегически.'
            ),
          ],
        },
        {
          key: 'branches',
          title: 'Ветви',
          rows: BRANCH_ITEMS.map((branch) => {
            const value = roundScore(scores[branch.key])

            return buildScaleRow(
                branch.key,
                branch.label,
                value,
                GREY_COLOR,
                null,
                branch.helpText
            )
          }),
        },
      ])
    }

    function buildEiDetailsProfile(data) {
      const details = data?.ei?.ei_abilities_detail ?? {}

      return applyStrengthGrowthProfileColors([
        {
          key: 'sections',
          title: 'Разделы',
          rows: SECTION_ITEMS.map((section) => {
            const value = roundScore(details[section.dataKey])

            return buildScaleRow(
                section.key,
                section.label,
                value,
                GREY_COLOR,
                null,
                DETAILS_HELP_TEXTS[section.key]
            )
          }),
        },
      ])
    }

    function buildFlags(flags, compareFlags = null) {
      const highestBalanced = getCount(flags?.group_balance?.highest_balanced)
      const highBalanced = getCount(flags?.group_balance?.high_balanced)

      const burnoutRisk = getCount(flags?.risks?.burnout_risk)
      const manipulativeRisk = getCount(flags?.risks?.manipulative_risk)
      const ledRisk = getCount(flags?.risks?.led_risk)
      const impulsivityRisk = getCount(flags?.risks?.impulsivity_risk)

      const moderateProfiles = getCount(flags?.moderate_profiles)

      const balancedTotal = highestBalanced + highBalanced
      const risksTotal = burnoutRisk + manipulativeRisk + ledRisk + impulsivityRisk

      return {
        groups: [
          {
            key: 'balanced',
            title: 'Сотрудники с балансом ЭИ',
            total: balancedTotal,
            color: '#21AC0C',
            items: [
              buildFlagItem(
                  'highest_balanced',
                  'В высоких границах, < 120',
                  highestBalanced,
                  flags?.group_balance?.highest_balanced,
                  compareFlags?.group_balance?.highest_balanced
              ),
              buildFlagItem(
                  'high_balanced',
                  'В средних границах, 100–120',
                  highBalanced,
                  flags?.group_balance?.high_balanced,
                  compareFlags?.group_balance?.high_balanced
              ),
            ],
          },
          {
            key: 'risks',
            title: 'Сотрудники с рисками',
            total: risksTotal,
            color: RED_COLOR,
            items: [
              buildFlagItem(
                  'burnout_risk',
                  'Выгорание',
                  burnoutRisk,
                  flags?.risks?.burnout_risk,
                  compareFlags?.risks?.burnout_risk
              ),
              buildFlagItem(
                  'manipulative_risk',
                  'Манипулятивность',
                  manipulativeRisk,
                  flags?.risks?.manipulative_risk,
                  compareFlags?.risks?.manipulative_risk
              ),
              buildFlagItem(
                  'led_risk',
                  'Не способен влиять на эмоции других',
                  ledRisk,
                  flags?.risks?.led_risk,
                  compareFlags?.risks?.led_risk
              ),
              buildFlagItem(
                  'impulsivity_risk',
                  'Риск импульсивности',
                  impulsivityRisk,
                  flags?.risks?.impulsivity_risk,
                  compareFlags?.risks?.impulsivity_risk
              ),
            ],
          },
          {
            key: 'moderate',
            title: 'Умеренный профиль',
            total: moderateProfiles,
            color: GREY_COLOR,
            items: [],
          },
        ],
        segments: [
          { key: 'balanced', value: balancedTotal, color: '#21AC0C' },
          { key: 'moderate', value: moderateProfiles, color: GREY_COLOR },
          { key: 'risks', value: risksTotal, color: RED_COLOR },
        ],
      }
    }

    function buildFlagItem(key, label, value, rawValue, rawCompareValue) {
      const item = {
        key,
        label,
        value,
      }

      const difference = getDifference(rawValue, rawCompareValue)

      if (difference !== null) {
        item.change = difference
      }

      return item
    }

    function buildArrayMap(items, keyField) {
      return items.reduce((result, item) => {
        result[item[keyField]] = item

        return result
      }, {})
    }

    function buildScaleRow(key, label, value, color, difference = null, helpText = '') {
      const row = {
        key,
        label,
        value,
        color,
        helpText,
      }

      if (difference !== null && difference !== undefined) {
        row.difference = difference
      }

      return row
    }

    return {
      isPreloader,
      isPrinting,
      openedSections,

      previousPeriod,
      currentPeriod,
      managementPotentialBars,
      eiGeneralScale,
      eiLevelBars,
      roleDistribution,
      domainDisbalance,
      strongSides,
      leadingBranches,
      growthZones,
      developmentPriorities,
      eiGrowthProfile,
      eiDetailsProfile,
      previousFlags,
      currentFlags,

      toggleSection,
      print,
      goBack,
      scrollTop,
      reportDetails
    }
  },
}
</script>

<style lang="scss" scoped>
.management-potential-report-page {
  width: 100%;
  background: #ffffff;
}

.management-potential-report-page__preloader {
  min-height: 280px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.management-potential-report-page__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  column-gap: 20px;
  margin-bottom: 32px;
}

.management-potential-report-page__title {
  margin: 0;
  font-weight: 600;
  font-size: 20px;
  line-height: 120%;
}

.management-potential-report-page__section {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.management-potential-report-page__chart-card {
  width: 100%;
  border-radius: 8px;
}

.management-potential-report-page__chart-card--small {
  max-width: 505px;
}

.management-potential-report-page__subsection {
  width: 100%;
}

.management-potential-report-page__subsection-title {
  margin: 0 0 16px;
  font-weight: 600;
  font-size: 16px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.85);
}

.management-potential-report-page__ei-chart-grid {
  width: 100%;
  display: grid;
  grid-template-columns: minmax(0, 505px) minmax(0, 1fr);
  gap: 16px;
  align-items: start;
}

.management-potential-report-page__notice {
  min-height: 34px;
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 8px 12px;
  border-radius: 4px;
  background: var(--geek-blue-1);
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.88);
}

.management-potential-report-page__notice-icon {
  flex: 0 0 auto;
  color: var(--geek-blue);
  font-size: 14px;
}

.management-potential-report-page__comparison-grid {
  width: 100%;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.management-potential-report-page__footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 4px;
}

.extended-filter-block {
  display: none;

  @media (max-width: $mobile-breakpoint) {
    margin: 24px 20px;
    display: flex;
    column-gap: 12px;
    align-items: center;
  }
}

.extended-filter-block h2 {
  font-size: 20px;
  margin-bottom: 0;
  font-weight: 600;
  line-height: 120%;
}

.back-button-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  min-width: 24px;
  min-height: 24px;
  background-color: #ffffff;
  border-radius: 25%;
  cursor: pointer;
}

@media (max-width: $tablet-breakpoint) {
  .management-potential-report-page__ei-chart-grid,
  .management-potential-report-page__comparison-grid {
    grid-template-columns: 1fr;
  }

  .management-potential-report-page__chart-card--small {
    max-width: none;
  }
}

@media (max-width: $mobile-breakpoint) {
  .management-potential-report-page__print {
    width: 100%;
  }

  .management-potential-report-page__header {
    align-items: flex-start;
    flex-direction: column;
    row-gap: 16px;
  }

  .management-potential-report-page__chart-card :deep(.ant-card-body) {
    padding: 14px 12px;
  }

  .management-potential-report-page__notice {
    align-items: flex-start;
    font-size: 13px;
  }

  .management-potential-report-page__footer {
    justify-content: stretch;
  }

  .management-potential-report-page__footer .secondary-button {
    width: 100%;
  }
}

.instruction-section {
  margin-bottom: 20px;
}
</style>
